import * as THREE from 'three'
import type Feature from 'types/feature'
import type PolyMesh from 'types/poly-mesh'
import Vector from 'types/vector'

const getFaceNormal = (face: Vector[]) => {
  const [p1, p2, p3] = face
  const edge1 = new THREE.Vector3(p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2])
  const edge2 = new THREE.Vector3(p3[0] - p1[0], p3[1] - p1[1], p3[2] - p1[2])

  const normal = edge1.cross(edge2).normalize().toArray()
  return normal
}

const polyMeshToFaceNormals = (polyMesh: PolyMesh) => {
  if (Boolean(polyMesh['face_normals'])) {
    return [...polyMesh.face_normals]
  }
  return (
    polyMesh?.faces?.map((face) => {
      const facePoints = face.map((point) => polyMesh.points[point])
      return getFaceNormal(facePoints)
    }) ?? []
  )
}

/**
 * Convert a PolyMesh into a BufferGeometry. Optionally group faces.
 *
 * @param polyMesh Points and faces.
 * @param features An array of features that will be used for grouping.
 * @param groups An array of grouped faces.
 */
type polyMeshToGeometryInput = {
  polyMesh: PolyMesh
  features?: Feature[]
  groupedFaces?: number[][]
}
export const polyMeshToGeometry = ({
  polyMesh,
  features = [],
  groupedFaces = [],
}: polyMeshToGeometryInput): THREE.BufferGeometry => {
  const geometry = new THREE.BufferGeometry()
  const positionAttrs = []
  const normalAttrs = []
  /**
   * the "faces" property in the poly mesh is a map of point indexes from the "points" property
   * this translates the face point map into real face normal vectors,
   * using the same face index for ordering.
   * it also acts as a proxy for surfaces that we need to create threejs groups from
   */
  const faceNormals = polyMeshToFaceNormals(polyMesh)

  let vertextCount = 0

  // add a vertex and a normal for each point in the face
  const addAttrs = (normal: Vector, faceIdx: number) => {
    const count = polyMesh.faces[faceIdx]?.length
    if (polyMesh.faces[faceIdx]) {
      for (let i = 0; i < polyMesh.faces[faceIdx].length; i++) {
        const point = polyMesh.faces[faceIdx][i]
        positionAttrs.push(...polyMesh.points[point])
        normalAttrs.push(...normal)
        vertextCount++
      }
    }

    return count
  }

  /**
   * Group the geometry.
   */

  // If features were passed in, pull each feature's faces to be used in the grouping logic below
  if (features.length) {
    groupedFaces = features.map((feature) => feature.faces)
  }

  if (groupedFaces) {
    for (
      let materialIndex = 0;
      materialIndex < groupedFaces.length;
      materialIndex++
    ) {
      const group = groupedFaces[materialIndex]
      const groupStart = vertextCount

      for (const faceIdx of group) {
        // if we haven't already created attrs for this face, do so
        if (Boolean(faceNormals[faceIdx])) {
          addAttrs(faceNormals[faceIdx], faceIdx)

          // remove the entry from the face normal list so
          // we know that we've created attrs for it
          delete faceNormals[faceIdx]
        }
      }
      const count = vertextCount - groupStart

      // create a group from the starting index of vertices
      geometry.addGroup(groupStart, count, materialIndex)
    }
  }

  /**
   * Add any remaining faces as a single group
   */
  const remainingFaces = faceNormals.filter(Boolean)
  if (remainingFaces.length) {
    const materialIndex = features.length
    const start = vertextCount

    faceNormals.map(addAttrs)
    const count = vertextCount - start

    geometry.addGroup(start, count, materialIndex)
  }

  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(positionAttrs), 3),
  )
  geometry.setAttribute(
    'normal',
    new THREE.BufferAttribute(new Float32Array(normalAttrs), 3),
  )

  return geometry
}
